import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Heart,
  Info,
  Trophy,
  Clock,
  HelpCircle,
  XCircle,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  runTransaction,
  increment,
} from "firebase/firestore";
import { db } from "../../utils/firebase";
import { useAuth } from "../../hooks/AuthenticationProvider";

const SnakeChallenge = () => {
  const navigate = useNavigate();
  const { user, organizationId } = useAuth();

  // ----------------------------------
  //          GAME STATES
  // ----------------------------------
  const [gameMode, setGameMode] = useState("intro"); // "intro" | "tutorial" | "play" | "results"
  const [difficulty, setDifficulty] = useState(1); // 1: Easy, 2: Medium, 3: Hard
  const [cellSize, setCellSize] = useState("1.5rem");
  const [gameStarted, setGameStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const timerRef = useRef(null);

  // ----------------------------------
  //         SNAKE GAME STATES
  // ----------------------------------
  const boardWidth = 15;
  const boardHeight = 15;
  // The snake is represented as an array of [row, col] positions.
  const [snake, setSnake] = useState([]);
  // The direction is an object with x (row movement) and y (column movement).
  const [direction, setDirection] = useState({ x: 0, y: 1 }); // Initially moving right.
  // Fruit's position on the board.
  const [fruit, setFruit] = useState([0, 0]);
  // Track fruits eaten.
  const [fruitsEaten, setFruitsEaten] = useState(0);
  // Mark if win condition (challenge complete) was reached.
  const [isComplete, setIsComplete] = useState(false);
  // Final result flag – only saving score if challenge was completed.
  const [gameWasCompleted, setGameWasCompleted] = useState(false);

  // Scoring & High Scores
  const [score, setScore] = useState(0);
  const [highScores, setHighScores] = useState([]);

  // UI / tutorial / messages
  const [message, setMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);

  // ----------------------------------
  //         HIGH SCORES FETCH
  // ----------------------------------
  useEffect(() => {
    if (!organizationId) return;
    const fetchScores = async () => {
      try {
        const docRef = doc(
          db,
          `organizations/${organizationId}/gameScores`,
          "snake-challenge"
        );
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const scoresForDifficulty =
            data.leaderboards && data.leaderboards[difficulty.toString()]
              ? data.leaderboards[difficulty.toString()]
              : [];
          setHighScores(scoresForDifficulty);
        } else {
          setHighScores([]);
        }
      } catch (err) {
        console.error("Error fetching high scores:", err);
      }
    };
    fetchScores();
  }, [organizationId, difficulty]);

  // ----------------------------------
  //          TIMER LOGIC
  // ----------------------------------
  useEffect(() => {
    if (isPlaying && !isPaused) {
      timerRef.current = setInterval(() => {
        setTimeElapsed((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPlaying, isPaused]);

  // ----------------------------------
  //         START / INITIALIZE GAME
  // ----------------------------------
  const startGame = () => {
    // Initialize snake: starting with three segments in the middle of the board.
    const midRow = Math.floor(boardHeight / 2);
    const midCol = Math.floor(boardWidth / 2);
    setSnake([
      [midRow, midCol],
      [midRow, midCol - 1],
      [midRow, midCol - 2],
    ]);
    // Set initial direction (moving right).
    setDirection({ x: 0, y: 1 });
    // Create the first fruit at a random position (not colliding with the snake).
    setFruit(
      getRandomFruitPosition([
        [midRow, midCol],
        [midRow, midCol - 1],
        [midRow, midCol - 2],
      ])
    );
    setFruitsEaten(0);
    setScore(0);
    setTimeElapsed(0);
    setIsComplete(false);
    setGameStarted(true);
    setIsPlaying(true);
    setIsPaused(false);
    setMessage("");
    setShowMessage(false);
  };

  // Generate a random position for the fruit that is not on the snake.
  const getRandomFruitPosition = (snakePositions) => {
    let newPos;
    while (true) {
      const row = Math.floor(Math.random() * boardHeight);
      const col = Math.floor(Math.random() * boardWidth);
      if (!snakePositions.some((pos) => pos[0] === row && pos[1] === col)) {
        newPos = [row, col];
        break;
      }
    }
    return newPos;
  };

  // ----------------------------------
  //         SNAKE MOVEMENT LOGIC
  // ----------------------------------
  useEffect(() => {
    if (isPlaying && !isPaused) {
      // Speed is dependent on difficulty.
      const speed = difficulty === 1 ? 175 : difficulty === 2 ? 140 : 100; // milliseconds per move
      const moveInterval = setInterval(() => {
        moveSnake();
      }, speed);
      return () => clearInterval(moveInterval);
    }
  }, [isPlaying, isPaused, snake, direction, difficulty]);

  useEffect(() => {
    const updateCellSize = () => {
      if (window.innerWidth < 640) {
        setCellSize("1rem");
      } else {
        setCellSize("1.5rem");
      }
    };
    updateCellSize();
    window.addEventListener("resize", updateCellSize);
    return () => window.removeEventListener("resize", updateCellSize);
  }, []);

  const moveSnake = () => {
    const head = snake[0];
    const newHead = [head[0] + direction.x, head[1] + direction.y];

    // Check for wall collision.
    if (
      newHead[0] < 0 ||
      newHead[0] >= boardHeight ||
      newHead[1] < 0 ||
      newHead[1] >= boardWidth
    ) {
      endGame();
      return;
    }

    // Check for self collision.
    if (
      snake.some(
        (segment) => segment[0] === newHead[0] && segment[1] === newHead[1]
      )
    ) {
      endGame();
      return;
    }

    // Update snake by adding the new head.
    const newSnake = [newHead, ...snake];
    const ateFruit = newHead[0] === fruit[0] && newHead[1] === fruit[1];
    if (ateFruit) {
      setFruitsEaten((prev) => prev + 1);
      // Increase score based on difficulty.
      const fruitScore = difficulty === 1 ? 50 : difficulty === 2 ? 75 : 100;
      setScore((prev) => prev + fruitScore);

      // Generate a new fruit (avoiding collision with the new snake).
      const newFruit = getRandomFruitPosition(newSnake);
      setFruit(newFruit);
    } else {
      // Normal move: remove the tail.
      newSnake.pop();
    }
    setSnake(newSnake);

    // Check win condition: target fruits eaten based on difficulty.
    const targetFruits = difficulty === 1 ? 5 : difficulty === 2 ? 10 : 15;
    if (ateFruit && fruitsEaten + 1 >= targetFruits) {
      setIsComplete(true);
      setMessage("Challenge Completed!");
      setShowMessage(true);
      setTimeout(() => {
        endGame();
      }, 1500);
    }
  };

  // ----------------------------------
  //         KEYBOARD CONTROLS
  // ----------------------------------
  useEffect(() => {
    if (isPlaying && !isPaused) {
      const handleKeyDown = (e) => {
        // Update direction via arrow keys while preventing reverse direction.
        if (e.key === "ArrowUp" && direction.x !== 1) {
          setDirection({ x: -1, y: 0 });
        } else if (e.key === "ArrowDown" && direction.x !== -1) {
          setDirection({ x: 1, y: 0 });
        } else if (e.key === "ArrowLeft" && direction.y !== 1) {
          setDirection({ x: 0, y: -1 });
        } else if (e.key === "ArrowRight" && direction.y !== -1) {
          setDirection({ x: 0, y: 1 });
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [isPlaying, isPaused, direction]);

  // ----------------------------------
  //            END GAME
  // ----------------------------------
  const endGame = () => {
    setIsPlaying(false);
    setGameMode("results");
    clearInterval(timerRef.current);

    // Simply use the accumulated score without a time multiplier.
    const finalScore = score;
    setScore(finalScore);
    setGameWasCompleted(isComplete);
  };

  // ----------------------------------
  //         TUTORIAL LOGIC
  // ----------------------------------
  const advanceTutorial = () => {
    if (tutorialStep < 3) {
      setTutorialStep((prev) => prev + 1);
    } else {
      setGameMode("play");
      startGame();
    }
  };

  // ----------------------------------
  //         SAVE SCORE TO FIREBASE
  // ----------------------------------
  const saveScore = async () => {
    if (!organizationId || !user?.email) return;

    try {
      // Determine userName from available fields.
      let userName = "Anonymous";
      if (user.firstName && user.lastName) {
        userName = `${user.firstName} ${user.lastName}`;
      } else if (user.displayName) {
        userName = user.displayName;
      } else if (user.email) {
        userName = user.email.split("@")[0];
      }

      const gameData = {
        gameType: "snake-challenge",
        gameId: "snake-challenge",
        mode: "single",
        userId: user.email,
        userName,
        score,
        difficulty,
        timeElapsed,
        fruitsEaten,
        isComplete,
        timestamp: new Date(),
      };

      const leaderboardDocRef = doc(
        db,
        `organizations/${organizationId}/gameScores`,
        "snake-challenge"
      );
      const userDocRef = doc(
        db,
        `organizations/${organizationId}/users`,
        user.email
      );

      // Composite comparator: score > lower time > completion status > timestamp
      const compositeComparator = (a, b) => {
        if (a.score !== b.score) return b.score - a.score;
        if (a.timeElapsed !== b.timeElapsed)
          return a.timeElapsed - b.timeElapsed;
        if (a.isComplete !== b.isComplete) return a.isComplete ? -1 : 1;
        return a.timestamp - b.timestamp;
      };

      await runTransaction(db, async (transaction) => {
        const leaderboardDoc = await transaction.get(leaderboardDocRef);
        let leaderboardData = leaderboardDoc.exists()
          ? leaderboardDoc.data()
          : { leaderboards: {} };

        const difficultyKey = difficulty.toString();
        let currentLeaderboard =
          leaderboardData.leaderboards[difficultyKey] || [];

        if (
          currentLeaderboard.length < 5 ||
          compositeComparator(
            gameData,
            currentLeaderboard[currentLeaderboard.length - 1]
          ) < 0
        ) {
          currentLeaderboard.push(gameData);
          currentLeaderboard.sort(compositeComparator);
          currentLeaderboard = currentLeaderboard.slice(0, 5);
          leaderboardData.leaderboards[difficultyKey] = currentLeaderboard;
          transaction.set(leaderboardDocRef, leaderboardData, { merge: true });
        }

        transaction.update(userDocRef, {
          gameScore: increment(score),
        });
      });

      // Save game history.
      const gameHistoryRef = doc(
        db,
        `organizations/${organizationId}/users/${
          user.email
        }/gameHistory/${Date.now()}`
      );
      await setDoc(gameHistoryRef, gameData);

      // Reset game state to intro.
      setGameMode("intro");
      setGameStarted(false);
    } catch (err) {
      console.error("Error saving Snake score:", err);
    }
  };

  // ----------------------------------
  //           FORMAT TIME
  // ----------------------------------
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // ----------------------------------
  //           RENDER
  // ----------------------------------
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 text-white p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/gameroom")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Gameroom</span>
          </button>

          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-yellow-300">
              Snake Challenge
            </h1>
            <div className="p-2 bg-yellow-500/10 rounded-full">
              <Heart className="text-yellow-400" size={20} />
            </div>
          </div>

          <button
            onClick={() => {}}
            className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <Info size={20} />
          </button>
        </div>

        {/* Instructions Modal (if needed) */}
        <AnimatePresence>
          {/* You can add a modal here similar to Sudoku if you want an overlay with instructions */}
        </AnimatePresence>

        {/* Outer Container */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
          {/* Intro Screen */}
          {gameMode === "intro" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Heart size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Snake Challenge
                </h2>
                <p className="text-slate-300 max-w-md mx-auto">
                  Guide your snake to eat fruits and grow while avoiding
                  collisions. Reach the target fruit count for your selected
                  difficulty to complete the challenge.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-yellow-500/10 rounded-full mb-2">
                    <Heart size={24} className="text-yellow-300" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Quick Reflexes
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Test your reaction time as the snake speeds up.
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-yellow-500/10 rounded-full mb-2">
                    <Clock size={24} className="text-yellow-300" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Timed Challenge
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Complete the challenge quickly to earn a time bonus.
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-yellow-500/10 rounded-full mb-2">
                    <Trophy size={24} className="text-yellow-300" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Multiple Difficulties
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Choose Easy, Medium, or Hard for different speed and target
                    goals.
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <div className="flex-1 max-w-xs">
                  <h3 className="font-medium text-white mb-2 text-center md:text-left">
                    Difficulty
                  </h3>
                  <div className="flex gap-2 justify-center md:justify-start">
                    {[1, 2, 3].map((lvl) => (
                      <button
                        key={lvl}
                        onClick={() => setDifficulty(lvl)}
                        className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                          difficulty === lvl
                            ? "bg-gradient-to-r from-yellow-400 to-orange-500 text-white"
                            : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                        }`}
                      >
                        {lvl === 1 ? "Easy" : lvl === 2 ? "Medium" : "Hard"}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setGameMode("tutorial")}
                    className="flex-1 py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <HelpCircle size={18} />
                    Tutorial
                  </button>

                  <button
                    onClick={() => setGameMode("play")}
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    Play Now
                  </button>
                </div>
              </div>

              {/* High Scores */}
              {highScores.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Trophy size={18} className="text-yellow-400" />
                    High Scores
                  </h3>
                  <div className="bg-slate-700/30 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm min-w-[500px]">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Rank
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Player
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Time
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Score
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {highScores.map((s, index) => {
                          let formattedName = s.userName;
                          if (s.userName && s.userName.includes(" ")) {
                            const parts = s.userName.split(" ");
                            formattedName = `${parts[0]} ${
                              parts[1] ? parts[1][0] + "." : ""
                            }`;
                          }
                          return (
                            <tr
                              key={`${s.userId}-${index}`}
                              className="border-t border-slate-700/50"
                            >
                              <td className="py-2 px-4 text-slate-300">
                                {index + 1}
                              </td>
                              <td className="py-2 px-4 text-white">
                                {formattedName}
                              </td>
                              <td className="py-2 px-4 text-center text-slate-300">
                                {formatTime(s.timeElapsed)}
                              </td>
                              <td className="py-2 px-4 text-center text-yellow-300 font-medium">
                                {s.score}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tutorial Screen */}
          {gameMode === "tutorial" && (
            <div className="p-6 md:p-8">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  Tutorial: Snake Challenge
                </h2>
                <div className="bg-slate-700/50 rounded-lg p-4 space-y-4 text-slate-300">
                  {tutorialStep === 0 && (
                    <>
                      <p>
                        In Snake Challenge, you control a snake that grows as it
                        eats fruits. Avoid hitting the walls or your own tail.
                      </p>
                    </>
                  )}
                  {tutorialStep === 1 && (
                    <>
                      <p>
                        Use the arrow keys (or on-screen buttons on mobile) to
                        change the snake's direction. The snake cannot reverse
                        direction instantly.
                      </p>
                    </>
                  )}
                  {tutorialStep === 2 && (
                    <>
                      <p>
                        Each fruit eaten increases your score and the length of
                        your snake. The game speeds up with higher difficulties.
                      </p>
                    </>
                  )}
                  {tutorialStep === 3 && (
                    <>
                      <p>
                        Complete the challenge by reaching the target number of
                        fruits: Easy: 5, Medium: 10, Hard: 15. A faster finish
                        gives you a time bonus.
                      </p>
                      <p>Ready? Let's slither!</p>
                    </>
                  )}
                </div>
              </div>

              <div className="flex justify-between">
                {tutorialStep > 0 ? (
                  <button
                    onClick={() => setTutorialStep((prev) => prev - 1)}
                    className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Previous
                  </button>
                ) : (
                  <button
                    onClick={() => setGameMode("intro")}
                    className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Back
                  </button>
                )}

                <button
                  onClick={advanceTutorial}
                  className="py-2 px-4 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-lg transition-colors"
                >
                  {tutorialStep < 3 ? "Next" : "Start Game"}
                </button>
              </div>
            </div>
          )}

          {/* Play Screen */}
          {gameMode === "play" && (
            <div className="relative p-6 md:p-8">
              {!gameStarted && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                  <button
                    onClick={() => {
                      setGameStarted(true);
                      startGame();
                    }}
                    className="py-3 px-6 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-lg"
                  >
                    Start Game
                  </button>
                </div>
              )}

              {/* Message Toast */}
              <AnimatePresence>
                {showMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-slate-800 px-4 py-2 rounded-lg shadow-lg z-40"
                  >
                    {message}
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex justify-between items-center mb-6">
                <div className="bg-gradient-to-r from-yellow-400/20 to-orange-500/20 px-4 py-2 rounded-lg">
                  <span className="text-yellow-200 font-medium">
                    Difficulty:{" "}
                    {difficulty === 1
                      ? "Easy"
                      : difficulty === 2
                      ? "Medium"
                      : "Hard"}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeElapsed)}
                    </span>
                  </div>
                  <button
                    onClick={() => setIsPaused(!isPaused)}
                    className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 hover:text-white transition-colors"
                  >
                    {isPaused ? "Resume" : "Pause"}
                  </button>
                </div>
              </div>

              {/* Game Board */}
              <div className="flex flex-col md:flex-row gap-8 items-start justify-center">
                <div className="bg-slate-800/70 border border-slate-700 rounded-xl p-1 md:p-2 mb-4 md:mb-0">
                  <div
                    className="grid"
                    style={{
                      gridTemplateColumns: `repeat(${boardWidth}, 1.5rem)`,
                    }}
                  >
                    {Array.from({ length: boardHeight }).map((_, rowIdx) =>
                      Array.from({ length: boardWidth }).map((_, colIdx) => {
                        const isSnake = snake.some(
                          (segment) =>
                            segment[0] === rowIdx && segment[1] === colIdx
                        );
                        const isHead =
                          snake.length > 0 &&
                          snake[0][0] === rowIdx &&
                          snake[0][1] === colIdx;
                        const isFruit =
                          fruit[0] === rowIdx && fruit[1] === colIdx;
                        // Determine cell styling.
                        let cellStyle = "w-6 h-6 border border-slate-700";
                        if (isHead) {
                          cellStyle += " bg-green-500";
                        } else if (isSnake) {
                          cellStyle += " bg-green-400";
                        } else if (isFruit) {
                          cellStyle += " bg-red-500";
                        } else {
                          cellStyle += " bg-slate-700";
                        }
                        return (
                          <div
                            key={`${rowIdx}-${colIdx}`}
                            className={cellStyle}
                          ></div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Controls */}
                <div className="md:w-52 space-y-6">
                  {/* Game Info */}
                  <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                    <h3 className="text-white font-medium mb-3">Game Info</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400 text-sm">
                          Fruits Eaten
                        </span>
                        <span className="text-yellow-300 font-medium">
                          {fruitsEaten}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400 text-sm">Score</span>
                        <span className="text-yellow-300 font-medium">
                          {score}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* On-screen Direction Buttons (optional, for mobile controls) */}
                  <div className="flex flex-col items-center">
                    <button
                      onClick={() => {
                        if (direction.x !== 1) setDirection({ x: -1, y: 0 });
                      }}
                      className={`
      mb-2 px-4 py-3
      text-white font-medium 
      rounded-lg 
      bg-gradient-to-r from-green-500 to-green-600
      hover:from-green-600 hover:to-green-700
      active:scale-95
      transform transition-all duration-150
      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500
    `}
                    >
                      <ChevronUp size={28} />
                    </button>

                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          if (direction.y !== 1) setDirection({ x: 0, y: -1 });
                        }}
                        className={`
        px-4 py-3
        text-white font-medium 
        rounded-lg 
        bg-gradient-to-r from-blue-500 to-blue-600
        hover:from-blue-600 hover:to-blue-700
        active:scale-95
        transform transition-all duration-150
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
      `}
                      >
                        <ChevronLeft size={28} />
                      </button>

                      <button
                        onClick={() => {
                          if (direction.y !== -1) setDirection({ x: 0, y: 1 });
                        }}
                        className={`
        px-4 py-3
        text-white font-medium 
        rounded-lg 
        bg-gradient-to-r from-blue-500 to-blue-600
        hover:from-blue-600 hover:to-blue-700
        active:scale-95
        transform transition-all duration-150
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
      `}
                      >
                        <ChevronRight size={28} />
                      </button>
                    </div>

                    <button
                      onClick={() => {
                        if (direction.x !== -1) setDirection({ x: 1, y: 0 });
                      }}
                      className={`
      mt-2 px-4 py-3
      text-white font-medium 
      rounded-lg 
      bg-gradient-to-r from-green-500 to-green-600
      hover:from-green-600 hover:to-green-700
      active:scale-95
      transform transition-all duration-150
      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500
    `}
                    >
                      <ChevronDown size={28} />
                    </button>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            "Are you sure you want to end the game early?"
                          )
                        ) {
                          endGame();
                        }
                      }}
                      className="w-full py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-colors text-sm"
                    >
                      End Game
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Challenge {isComplete ? "Completed!" : "Over"}
                </h2>
                <p className="text-slate-300">
                  Your Snake Challenge session has ended.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-yellow-300" />
                    Your Results
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-yellow-300">
                        {score}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Difficulty</span>
                      <span className="text-white font-medium">
                        {difficulty === 1
                          ? "Easy"
                          : difficulty === 2
                          ? "Medium"
                          : "Hard"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Completion</span>
                      <span
                        className={`font-medium ${
                          isComplete ? "text-green-300" : "text-red-300"
                        }`}
                      >
                        {isComplete ? "Complete" : "Incomplete"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Time Spent</span>
                      <span className="text-white">
                        {formatTime(timeElapsed)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Fruits Eaten</span>
                      <span className="text-yellow-300 font-medium">
                        {fruitsEaten}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Heart size={20} className="text-yellow-300" />
                    Cognitive Benefits
                  </h3>
                  <div className="space-y-3 text-slate-300">
                    <p>
                      <span className="text-yellow-300 font-medium">
                        Reaction Time:
                      </span>{" "}
                      Quick decision making improves your reflexes.
                    </p>
                    <p>
                      <span className="text-yellow-300 font-medium">
                        Strategic Planning:
                      </span>{" "}
                      Plan your moves to maximize growth and avoid collisions.
                    </p>
                    <p>
                      <span className="text-yellow-300 font-medium">
                        Hand–Eye Coordination:
                      </span>{" "}
                      Better coordination as you navigate the snake.
                    </p>
                    <p className="mt-4 text-sm">
                      Regular practice with games like Snake can sharpen these
                      skills.
                    </p>
                  </div>
                </div>
              </div>

              {/* Scoring Explanation */}
              <div className="mb-6 bg-slate-800/50 rounded-lg p-4 text-sm text-slate-300">
                <h3 className="text-white font-medium mb-2">
                  How Scoring Works
                </h3>
                <p>
                  Base Score: (Fruits eaten ×{" "}
                  {difficulty === 1 ? 100 : difficulty === 2 ? 150 : 200})<br />
                  Time Bonus: Calculated based on completion speed
                  <br />
                  {!isComplete && (
                    <span className="text-red-300">
                      Incomplete challenge: reduced score
                    </span>
                  )}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button
                  onClick={() => setGameMode("intro")}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Back to Menu
                </button>

                {gameWasCompleted && (
                  <button
                    onClick={saveScore}
                    className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    <Trophy size={18} />
                    Save Score
                  </button>
                )}

                <button
                  onClick={() => {
                    setGameMode("play");
                    startGame();
                  }}
                  className="py-3 px-6 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  Play Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SnakeChallenge;
